import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  CardContent,
  CardActions,
  LinearProgress,
  Collapse,
  DialogActions,
  IconButton,
  Paper,
  Popover,
  Divider,
  DialogContent,
  Autocomplete,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import {
  getDealPipelines,
  getAllDealStages,
  addDealStage,
  updateDealStage,
  deleteDealStage,
  savePipeline,
  getAPipeline,
  deleteAPipeline,
  updatePipeline,
  postStageTransfer,
  getStageTransfer,
  updateStageTransfer,
  RuleAlreadyExistCheck,
} from "../../../redux/modules/deals";
import AccessControl from "../../../components/AccessControl";
import DialogTitle from "../../../components/DialogTitle";
import DealEmailTemplate from "./DealEmailTemplate";
import { getDealTemplates } from "../../../redux/modules/deals";
import { parseTimeFrame } from "../../../utils/format_date";
import { Box } from "@mui/material";
import { formatTime } from "../../../utils/timeConverter";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EmailIcon from "@mui/icons-material/Email";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BadgeIcon from "@mui/icons-material/Badge";
import { setAlert } from "../../../redux/modules/alert";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { getBTemplates } from "../../../redux/modules/email";

function checkEmptyIn(stages) {
  return !!stages.filter((s) => !s.stage_name).length;
}

function StageItem({
  stage,
  index,
  cancelNew,
  isNew,
  addNew,
  update,
  remove,
  updateDealStagesState,
}) {
  const dispatch = useDispatch();

  const [editable, setEditable] = useState(isNew);
  const [name, setName] = useState(stage.stage_name);
  const [disableSave, setDisableSave] = useState(!name);
  const [anchorEl, setAnchorEl] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openFU, setOpenFU] = useState(false);

  const handleStageNameChange = (e) => {
    const value = e.target.value;
    const save = !value.trim() || value.trim() === stage.stage_name;
    if (disableSave != save) {
      setDisableSave(save);
    }
    setName(value);
  };

  const onEdit = () => {
    setEditable(true);
    setDisableSave(true);
  };

  const onCancel = () => {
    if (isNew) {
      cancelNew(index);
    } else {
      setEditable(false);
      setName(stage.stage_name);
    }
  };

  const onDelete = (e) => {
    e.persist();
    setAnchorEl(e.currentTarget);
    remove(stage.stage_id)
      .then((dss) => {
        updateDealStagesState(dss);
      })
      .catch((error) => {
        if (error.status === 0) {
          setCount(error.count);
        }
      });
  };

  const onSave = () => {
    if (isNew) {
      addNew(name).then((dss) => {
        setEditable(false);
        updateDealStagesState(dss);
      });
    } else {
      update({ ...stage, stage_name: name }).then((dss) => {
        setEditable(false);
        updateDealStagesState(dss);
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDealtempOpen = () => {
    setOpenFU(false);
  };

  const onDealtempClose = () => {
    setOpenFU(false);
  };

  return (
    <div className="role_item">
      {editable ? (
        <TextField
          size="small"
          className="text-field"
          name="item"
          label="Stage"
          variant="outlined"
          onChange={handleStageNameChange}
          value={name}
          required
        />
      ) : (
        <Typography className="role_name">{stage.stage_name}</Typography>
      )}
      {editable ? (
        <div className="save_cancel_wrapper">
          <Button onClick={onSave} disabled={disableSave}>
            Save
          </Button>
          <Button onClick={onCancel} style={{ color: "#000" }}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="save_cancel_wrapper">
          <Button className="bdr-btn-style-green" onClick={onEdit}>
            Edit
          </Button>
          <Button className="bdr-btn-style-chu" onClick={onDelete}>
            Delete
          </Button>
          <Popover
            id={stage.stage_id}
            open={!!count && !!anchorEl}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <Typography
              style={{ padding: 15, maxWidth: 200 }}
            >{`You cannot delete this deal stage, since ${`${count} ${
              Number(count) === 1 ? "deal pipeline is" : "deal pipelines are"
            }`} using this stage.`}</Typography>
          </Popover>
        </div>
      )}
      <DealEmailTemplate
        open={openFU}
        close={onDealtempClose}
        lead={{}}
        loadingDU={loading}
        multiple
        stage={stage}
      />
    </div>
  );
}

function PipelineForm({
  cancel,
  pipeline_id,
  dealStages,
  pipeline_stages,
  pipeline_name,
  default: defaultValue,
  deal_expiry,
  newPipe,
}) {
  const [name, setName] = useState(newPipe ? "" : pipeline_name || "");
  const [dealExpiry, setDealExpiry] = useState(deal_expiry || 0);
  const [defaultPipe, setDefaultPipe] = useState(!!Number(defaultValue));
  const [dStages, setDStages] = useState(
    pipeline_stages || [{ stage_name: "", probability: 0 }]
  );
  const [disableAS, setDisableAS] = useState(checkEmptyIn(dStages));
  const [disableSave, setDisableSave] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();
  const bodyTemplates = useSelector((state) => state?.email?.bodyTemplates);
  const anchorRef = useRef(null);
  const theme = useTheme();
  // transfer section
  const [expandedStages, setExpandedStages] = useState({});
  const [transferData, setTransferData] = useState({
    id: "",
    emailTemplateId: "",
    followUpTime: "",
    followUpUnit: "",
    moveToStage: "",
    autoMoveTime: "",
    autoMoveUnit: "",
    senderName: "",
  });

  const [fromStage, setFromStage] = useState(null);
  const [toStage, setToStage] = useState(null);
  const [completedStages, setCompletedStages] = useState({});
  const [isUpdate, setIsUpdate] = useState({});
  const [openTemplate, setOpenTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStageIndex, setActiveStageIndex] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const onStageNameChange = (value, s) => {
    const existInOptions = dealStages.find((ds) => ds.stage_name == value);
    if (existInOptions) {
      const uStages = [...dStages];
      uStages[s] = { ...dStages[s], ...existInOptions };

      return setDStages(uStages);
    }
    const valueNotChanged =
      pipeline_stages &&
      pipeline_stages[s] &&
      pipeline_stages[s]["stage_name"] === dStages[s]["stage_name"];
    const newStageID = !valueNotChanged ? { stage_id: "" } : {};

    const updatedStages = [...dStages];
    updatedStages[s] = { ...dStages[s], ...newStageID, stage_name: value };
    setDStages(updatedStages);
    setDisableAS(checkEmptyIn(updatedStages));
    setDisableSave(!name || checkEmptyIn(updatedStages));
  };

  const onStageNameSelect = (e, v, s) => {
    const updatedStages = [...dStages];
    updatedStages[s] = {
      ...dStages[s],
      ...(v || { stage_name: "", stage_id: "" }),
    };
    setDisableAS(checkEmptyIn(updatedStages));
    setDStages(updatedStages);
    setDisableSave(checkEmptyIn(updatedStages));
  };

  const onAddNewStage = () => {
    const updatedStages = [...dStages];
    updatedStages.push({ stage_name: "", probability: 0 });
    setDStages(updatedStages);
    setDisableAS(checkEmptyIn(updatedStages));
    setDisableSave(!name || checkEmptyIn(updatedStages));
  };

  const onProbChange = (value, s) => {
    const updatedStages = [...dStages];
    updatedStages[s] = { ...dStages[s], probability: value };

    setDStages(updatedStages);
    setDisableAS(checkEmptyIn(updatedStages));
  };

  const onPipelineNameChange = (e) => {
    const newName = e.target.value;

    setName(newName);
    setDisableSave(!newName || checkEmptyIn(dStages));
  };

  const onDefaultCheck = (e) => {
    setDefaultPipe(!!e.target.checked);
  };

  const onExpiryChange = (e) => {
    setDealExpiry(e.target.value);
  };

  const onFormCancel = () => {
    cancel();
    setDStages(pipeline_stages || [{ stage_name: "", probability: 0 }]);
  };

  const resetState = () => {
    setName("");
    setDefaultPipe(false);
    setDealExpiry(0);
    setDStages([{ stage_name: "", probability: 0 }]);
  };

  const onSaveNewPipeline = () => {
    // setIsSaving(true);
    const newPipeline = {
      pipeline_name: name,
      deal_expiry: dealExpiry,
      default: defaultPipe ? 1 : 0,
      pipeline_stages: dStages,
    };
    let promise = Promise.resolve();

    if (newPipe) {
      promise = dispatch(savePipeline(newPipeline)).then(() => {
        setShowAlert(true);
        resetState();
      });
    } else {
      promise = dispatch(updatePipeline({ pipeline_id, ...newPipeline }));
    }

    promise.then(() => {
      // setIsSaving(false);
      dispatch(getAllDealStages());
      cancel();
    });
  };

  const autoSuggestStages = dealStages.filter(
    (dsf) => !dStages.filter((dss) => dss.stage_id == dsf.stage_id).length
  );

  // Updated handleSwitchToggle function
  const handleSwitchToggle = (s, stage) => {
    dispatch(getBTemplates());

    const stageId = stage.stage_id; // Use stage_id as the key

    // Toggle logic: Close if already open, otherwise close all and open this one
    setExpandedStages((prev) => {
      if (prev[stageId]) {
        // If current stage is open, close it
        return { ...prev, [stageId]: false };
      } else {
        // Close all stages and open only the clicked one
        const newState = {};
        Object.keys(prev).forEach((key) => {
          newState[key] = false;
        });
        newState[stageId] = true;
        return newState;
      }
    });

    // Only fetch data if we're opening a stage (not closing)
    if (!expandedStages[stageId]) {
      dispatch(getStageTransfer(pipeline_id)).then((response) => {
        const { result } = response;
        const stageData = result?.find(
          (item) => item.deal_stage_from === stage.stage_id
        );

        if (stageData) {
          const formattedFollowup = parseTimeFrame(
            stageData.follow_up_time || ""
          );
          const formattedAutomove = parseTimeFrame(
            stageData.stage_transition_time || ""
          );
          const activeTemplateId = stageData?.template_id;

          setTransferData((prevData) => {
            const updatedStageData = {
              ...(prevData[stage.stage_id] || {}),
              followUpTime: formattedFollowup?.value,
              followUpUnit:
                formattedFollowup?.unit === "h"
                  ? "hours"
                  : formattedFollowup?.unit === "m"
                  ? "minute"
                  : "days",
              autoMoveTime: formattedAutomove?.value,
              autoMoveUnit:
                formattedAutomove?.unit === "h"
                  ? "hours"
                  : formattedAutomove?.unit === "m"
                  ? "minute"
                  : "days",
              moveToStage: stageData?.deal_stage_to || "",
              emailTemplateId:
                activeTemplateId ||
                prevData[stage.stage_id]?.emailTemplateId ||
                "",
              senderName: stageData?.sender_name,
              id: stageData?.id,
            };
            return {
              ...prevData,
              [stage.stage_id]: updatedStageData,
            };
          });

          setIsUpdate((prev) => ({ ...prev, [s]: true }));
        } else {
          setTransferData((prevData) => ({
            ...prevData,
            [stage.stage_id]: prevData[stage.stage_id] || {
              emailTemplateId: "",
              followUpTime: "",
              followUpUnit: "",
              moveToStage: "",
              autoMoveTime: "",
              autoMoveUnit: "",
            },
          }));

          setIsUpdate((prev) => ({ ...prev, [s]: false }));
        }
      });

      // Set the "from stage" and "to stage" when opening
      setFromStage(stage);
      setToStage(dStages[s + 1] || null);
    }
  };

  const onStageRemove = async (index) => {
    const stageToRemove = dStages[index];
    if (!stageToRemove) return;

    const stageIdToRemove = stageToRemove.stage_id;

    // Local delete handler
    const handleLocalDelete = () => {
      const updatedStages = [...dStages];
      updatedStages.splice(index, 1);
      setDStages(updatedStages);

      // Clean up transferData
      setTransferData((prev) => {
        const copy = { ...prev };
        delete copy[stageIdToRemove];
        Object.keys(copy).forEach((stageId) => {
          if (copy[stageId].moveToStage === stageIdToRemove) {
            copy[stageId] = {
              ...copy[stageId],
              moveToStage: "",
              autoMoveTime: "",
              autoMoveUnit: "",
              emailTemplateId: "",
              followUpTime: "",
              followUpUnit: "",
            };
          }
        });
        return copy;
      });

      setExpandedStages((prev) => {
        const copy = { ...prev };

        // Remove deleted stage toggle
        delete copy[stageIdToRemove];

        // Find all stage IDs in order
        const stageIds = dStages.map((stage) => stage.stage_id);

        // Find the index of the deleted stage
        const deletedIndex = stageIds.indexOf(stageIdToRemove);

        // Collapse the previous stage if it exists and was expanded
        if (deletedIndex > 0) {
          const prevStageId = stageIds[deletedIndex - 1];
          if (copy[prevStageId]) {
            copy[prevStageId] = false;
          }
        }

        return copy;
      });

      // Clean up isUpdate
      setIsUpdate((prev) => {
        const copy = { ...prev };
        delete copy[stageIdToRemove];
        return copy;
      });

      setDisableAS(checkEmptyIn(updatedStages));
      setDisableSave(
        !name || !updatedStages.length || checkEmptyIn(updatedStages)
      );
    };

    // Skip API if no pipeline_id or stage_id
    if (!pipeline_id || !stageIdToRemove) {
      handleLocalDelete();
      return;
    }

    try {
      const res = await dispatch(
        RuleAlreadyExistCheck(stageIdToRemove, pipeline_id)
      );
      const { rule_exist } = res;

      if (rule_exist) {
        dispatch(
          setAlert(
            "This stage cannot be deleted because a transition rule is already configured against it.",
            "info"
          )
        );
        return;
      }
      // Safe to delete
      handleLocalDelete();
    } catch (error) {
      console.error("Error while checking rule existence:", error);
      dispatch(
        setAlert(
          "Something went wrong while checking stage dependencies. Please try again.",
          "error"
        )
      );
    }
  };

  const handleInputChange = (stageIndex, field, value) => {
    setTransferData((prevData) => {
      const newData = {
        ...prevData,
        [stageIndex]: {
          ...(prevData[stageIndex] || {}),
          [field]: value,
        },
      };

      return newData;
    });
  };

  const onSaveTransfertime = (stageIndex) => {
    const {
      autoMoveTime,
      autoMoveUnit,
      followUpTime,
      followUpUnit,
      moveToStage,
      emailTemplateId,
      senderName,
    } = transferData[stageIndex];
    // Format the time (e.g., "1D" or "1H")
    const formattedAutoMoveTime = `${autoMoveTime}${
      autoMoveUnit === "days" ? "d" : autoMoveUnit === "hours" ? "h" : "m"
    }`;
    const formattedFollowUpTime = `${followUpTime}${
      followUpUnit === "days" ? "d" : followUpUnit === "hours" ? "h" : "m"
    }`;
    const postData = {
      pipeline_id: pipeline_id,
      deal_stage_from: fromStage?.stage_id,
      deal_stage_to: moveToStage,
      stage_transition_time: formattedAutoMoveTime,
      follow_up_time: formattedFollowUpTime,
      template_id: emailTemplateId,
      ...(senderName && { sender_name: senderName }),
    };

    dispatch(postStageTransfer(postData))
      .then((res) => {
        setExpandedStages({});
      })
      .catch((error) => {
        dispatch(setAlert(error?.message || `Error in inserting`, "error"));
      });

    setCompletedStages((prev) => ({
      ...prev,
      [fromStage.stage_name]: true,
    }));
  };

  const onUpdateTransferTime = (stageIndex) => {
    const {
      autoMoveTime,
      autoMoveUnit,
      followUpTime,
      followUpUnit,
      moveToStage,
      emailTemplateId,
      senderName,
      id,
    } = transferData[stageIndex];
    const formattedAutoMoveTime = `${autoMoveTime}${
      autoMoveUnit === "days" ? "d" : autoMoveUnit === "hours" ? "h" : "m"
    }`;
    const formattedFollowUpTime = `${followUpTime}${
      followUpUnit === "days" ? "d" : followUpUnit === "hours" ? "h" : "m"
    }`;
    const UpdateData = {
      deal_stage_from: fromStage?.stage_id,
      deal_stage_to: moveToStage,
      stage_transition_time: formattedAutoMoveTime,
      follow_up_time: formattedFollowUpTime,
      template_id: emailTemplateId,
      sender_name: senderName,
      tbl_id: id,
    };

    dispatch(updateStageTransfer(UpdateData))
      .then((res) => {
        setExpandedStages({});
      })
      .catch((error) => {
        dispatch(setAlert(error?.message || `Error in inserting`, "error"));
      });
  };

  const onEmailtempOpen = (stage) => {
    setOpenTemplate(true);
  };

  const onEmailtempClose = () => {
    setOpenTemplate(false);
  };

  const isTransferDataValid = (data) => {
    return (
      data?.emailTemplateId &&
      data?.followUpTime &&
      data?.followUpUnit &&
      data?.moveToStage &&
      data?.autoMoveTime &&
      data?.autoMoveUnit
    );
  };

  const handleCancel = (stageIndex) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageIndex]: false,
    }));
    setTransferData({});
  };

  const handleAddNewTemplateClick = (stageIndex) => {
    setActiveStageIndex(stageIndex);
  };

  return (
    <>
      {/* Hidden anchor element at center */}
      <div
        ref={anchorRef}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 0,
          height: 0,
        }}
      />

      <Popover
        open={showAlert}
        anchorEl={anchorRef.current}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 0,
            maxWidth: 320,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* Heading bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
            px: 1.5,
            py: 1,
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Configuring Your Pipeline
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setShowAlert(false)}
            sx={{ color: theme.palette.info.contrastText }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body content */}
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            We can only configure the rule against a pipeline.
            <br />
            <br />
            1. Click <strong>Edit</strong>.<br />
            2. Use the toggle switch in each stage row to configure rules.
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            Tip: This step ensures your stages are fully configured.
          </Typography>
        </Box>
      </Popover>

      <Card style={{ marginBottom: 15 }}>
        <CardContent>
          <Typography variant="h6" style={{ marginBottom: 15 }}>
            {newPipe ? "Add new pipeline" : "Update pipeline"}
          </Typography>
          <Typography>Pipeline Name</Typography>
          <TextField
            size="small"
            variant="outlined"
            margin="dense"
            style={{ width: "100%" }}
            onChange={onPipelineNameChange}
            value={name}
          />
          <Checkbox onChange={onDefaultCheck} checked={defaultPipe} />
          <Typography component="span">Mark as default pipeline</Typography>
          <Typography>Deal Expiry</Typography>
          <div style={{ display: "flex", alignItems: "center" }}>
            <TextField
              size="small"
              variant="outlined"
              margin="dense"
              type="number"
              onChange={onExpiryChange}
              value={dealExpiry}
            />
            <Typography component="span" style={{ marginLeft: 15 }}>
              Days
            </Typography>
          </div>
          <Typography>Deal Stages</Typography>
          <Paper
            style={{
              display: "grid",
              gridTemplateColumns: `${pipeline_id ? "55%" : "70%"} 20% 10% 10%`, // Adjusted to add space for the Switch
              padding: 16,
              marginTop: 10,
            }}
          >
            <Typography variant="caption">Stages</Typography>
            <Typography variant="caption" style={{ marginLeft: 5 }}>
              Probability (%)
            </Typography>
            <Typography variant="caption"></Typography>

            {pipeline_id ? (
              <Typography variant="caption">Configure</Typography>
            ) : (
              <Typography variant="caption"></Typography>
            )}

            {/* Empty space for switch label */}
            {dStages.map((st, s) => (
              <React.Fragment key={s}>
                <Autocomplete
                  onChange={(e, v) => onStageNameSelect(e, v, s)}
                  onInputChange={(e, v) => onStageNameChange(v, s)}
                  options={autoSuggestStages}
                  getOptionLabel={(option) => option.stage_name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      margin="dense"
                    />
                  )}
                  value={st}
                  freeSolo
                />
                <TextField
                  size="small"
                  variant="outlined"
                  margin="dense"
                  type="number"
                  style={{ marginLeft: 10 }}
                  onChange={(e) => onProbChange(e.target.value, s)}
                  value={st.probability}
                />
                <IconButton
                  style={{ marginLeft: 10 }}
                  onClick={() => onStageRemove(s)}
                  disabled={Number(st.used) > 0}
                >
                  <RemoveCircleOutline />
                </IconButton>
                <FormGroup style={{ marginTop: "5px", marginLeft: 10 }}>
                  {pipeline_id && (
                    <Tooltip
                      placement="left"
                      arrow
                      enterDelay={300}
                      leaveDelay={200}
                      disableInteractive={false} // allow hover on tooltip
                      PopperProps={{
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [0, 8], // Push tooltip 8px away from toggle
                            },
                          },
                        ],
                        sx: {
                          pointerEvents: "auto", // Allow hovering on tooltip
                        },
                      }}
                      title={
                        !st.stage_id ? (
                          <div
                            style={{
                              maxWidth: 150,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Configuration Not Available</strong>
                            <br />
                            Please complete this stage setup first by selecting
                            a stage name to enable configuration.
                          </div>
                        ) : s === dStages.length - 1 ? (
                          <div
                            style={{
                              maxWidth: 100,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Not Available</strong>
                            <br />
                            This is the last stage, so no "next stage" exists
                            for Auto‑Move or Follow‑Up configuration.
                          </div>
                        ) : !dStages
                            .slice(s + 1)
                            .some((stage) => stage.stage_id) ? (
                          <div
                            style={{
                              maxWidth: 150,
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Configuration Not Available</strong>
                            <br />
                            Please add and configure at least one stage after
                            this one to enable Auto‑Move settings.
                          </div>
                        ) : (
                          <div>
                            <strong>Configure Follow‑Up & Auto‑Move</strong>
                            <br />
                            Enable this to set:
                            <ul style={{ paddingLeft: 18, margin: "4px 0" }}>
                              <li>Follow‑Up Email Rules</li>
                              <li>Auto‑Move Stage Timing</li>
                            </ul>
                          </div>
                        )
                      }
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={expandedStages[st.stage_id] || false}
                            onChange={() => handleSwitchToggle(s, st)}
                            // disabled={
                            //   s === dStages.length - 1 // Disable toggle for the last stage (no "to stage")
                            // }
                            disabled={
                              !st.stage_id || // Disable if current stage doesn't have stage_id
                              s === dStages.length - 1 || // Disable for the last stage
                              !dStages
                                .slice(s + 1)
                                .some((stage) => stage.stage_id) // Disable if no valid stage exists after current stage
                            }
                          />
                        }
                      />
                    </Tooltip>
                  )}
                </FormGroup>

                {expandedStages[st.stage_id] && (
                  <Paper
                    style={{
                      gridColumn: "1 / span 4",
                      marginTop: 10,
                      paddingLeft: 15,
                      paddingRight: 15,
                      marginBottom: 10,
                      paddingTop: 10,
                      paddingBottom: 10,
                    }}
                  >
                    {/* Overlayed Section for Follow-Up and Auto-Move Logic */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: 12,
                        flexDirection: "column",
                        marginBottom: 20,
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        Auto-Move Settings
                      </Typography>

                      <Select
                        size="small"
                        // value={transferData[st.stage_id]?.moveToStage || ""}
                        value={
                          dStages.some(
                            (stage) =>
                              stage.stage_id ===
                              transferData[st.stage_id]?.moveToStage
                          )
                            ? transferData[st.stage_id]?.moveToStage
                            : transferData[st.stage_id]?.moveToStage
                            ? ""
                            : ""
                        }
                        onChange={(e) =>
                          handleInputChange(
                            st.stage_id,
                            "moveToStage",
                            e.target.value
                          )
                        }
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select Stage
                        </MenuItem>
                        {dStages
                          .filter((_, index) => index > s) // 👈 Only show stages after current
                          .map((stage) => (
                            <MenuItem
                              key={stage.stage_id}
                              value={stage.stage_id}
                            >
                              {stage.stage_name}
                            </MenuItem>
                          ))}
                      </Select>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: "10px",
                          width: "100%",
                        }}
                      >
                        <div style={{ width: "62%" }}>
                          <TextField
                            label="After"
                            type="number"
                            size="small"
                            value={
                              transferData[st.stage_id]?.autoMoveTime || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                st.stage_id,
                                "autoMoveTime",
                                e.target.value
                              )
                            }
                            sx={{ width: "100%" }}
                            inputProps={{
                              min: 1,
                              onWheel: (e) => e.target.blur(),
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" || // prevent negative
                                e.key === "e" || // prevent scientific notation
                                e.key === "." || // prevent decimals
                                (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                        <div style={{ width: "35%" }}>
                          <Select
                            size="small"
                            value={
                              transferData[st.stage_id]?.autoMoveUnit || ""
                            }
                            displayEmpty
                            sx={{
                              width: "100%",
                            }}
                            onChange={(e) =>
                              handleInputChange(
                                st.stage_id,
                                "autoMoveUnit",
                                e.target.value
                              )
                            }
                            inputProps={{
                              min: 1,
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" || // prevent negative
                                e.key === "e" || // prevent scientific notation
                                e.key === "." || // prevent decimals
                                (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                              ) {
                                e.preventDefault();
                              }
                            }}
                            error={
                              Boolean(
                                transferData[st.stage_id]?.autoMoveTime
                              ) && !transferData[st.stage_id]?.autoMoveUnit
                            }
                          >
                            <MenuItem value="" disabled>
                              Select Unit
                            </MenuItem>
                            <MenuItem value="days">Day</MenuItem>
                            <MenuItem value="hours">Hour</MenuItem>
                            <MenuItem value="minute">Minute</MenuItem>
                          </Select>
                        </div>
                      </Box>
                    </div>
                    <div>
                      <Typography variant="caption" fontWeight="bold">
                        Follow-Up Email Settings
                      </Typography>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: "column",
                          gap: "10px",
                          marginTop: 8,
                        }}
                      >
                        <Select
                          size="small"
                          value={
                            transferData[st.stage_id]?.emailTemplateId || ""
                          }
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span>Select Email Template</span>;
                            }
                            const template = bodyTemplates.find(
                              (t) => String(t.tb_id) === selected
                            );
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography>{template?.name}</Typography>
                                <Box
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color:
                                      template?.is_plain_email === "1"
                                        ? "#2e7d32"
                                        : "#1565c0",
                                    backgroundColor:
                                      template?.is_plain_email === "1"
                                        ? "#E8F5E9"
                                        : "#E3F2FD",
                                    borderRadius: "10px",
                                    px: 1,
                                    py: "2px",
                                  }}
                                >
                                  {template.is_plain_email === "1"
                                    ? "Plain"
                                    : "Branded"}
                                </Box>
                              </Box>
                            );
                          }}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "add_new_template") {
                              onEmailtempOpen(st);
                              handleAddNewTemplateClick(s);
                            } else {
                              handleInputChange(
                                st.stage_id,
                                "emailTemplateId",
                                String(value)
                              );
                            }
                          }}
                        >
                          {bodyTemplates?.length > 0
                            ? [
                                <MenuItem key="placeholder" value="" disabled>
                                  Select Email Template
                                </MenuItem>,
                                <MenuItem
                                  value="add_new_template"
                                  sx={{ color: "#1976d2" }}
                                >
                                  <AddCircleOutline
                                    fontSize="small"
                                    sx={{ mr: 1 }}
                                  />{" "}
                                  Add New Template
                                </MenuItem>,
                                <MenuItem disabled>
                                  <hr
                                    style={{
                                      width: "100%",
                                      border: "none",
                                      borderTop: "1px solid #ddd",
                                    }}
                                  />
                                </MenuItem>,
                                ...bodyTemplates.map((template) => (
                                  <MenuItem
                                    key={template.tb_id}
                                    value={String(template.tb_id)}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",
                                      }}
                                    >
                                      <Typography>{template?.name}</Typography>
                                      <Box
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: 600,
                                          color:
                                            template.is_plain_email === "1"
                                              ? "#2e7d32"
                                              : "#1565c0",
                                          backgroundColor:
                                            template.is_plain_email === "1"
                                              ? "#E8F5E9"
                                              : "#E3F2FD",
                                          borderRadius: "10px",
                                          px: 1,
                                          py: "2px",
                                        }}
                                      >
                                        {template.is_plain_email === "1"
                                          ? "Plain"
                                          : "Branded"}
                                      </Box>
                                    </Box>
                                  </MenuItem>
                                )),
                              ]
                            : [
                                <MenuItem key="not-found" disabled>
                                  No templates available
                                </MenuItem>,
                              ]}
                        </Select>

                        <TextField
                          label="Email Sender Name"
                          type="text"
                          size="small"
                          placeholder="Leave empty to use default company name"
                          value={transferData[st.stage_id]?.senderName || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filteredValue = value
                              .replace(/[^a-zA-Z\s]/g, "")
                              .slice(0, 50);
                            handleInputChange(
                              st.stage_id,
                              "senderName",
                              filteredValue
                            );
                          }}
                          style={{ width: "100%" }}
                          inputProps={{
                            maxLength: 50,
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "10px",
                            width: "100%",
                          }}
                        >
                          <TextField
                            label="Send Email After"
                            type="number"
                            size="small"
                            value={
                              transferData[st.stage_id]?.followUpTime || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                st.stage_id,
                                "followUpTime",
                                e.target.value
                              )
                            }
                            style={{ width: "60%" }}
                            inputProps={{
                              min: 1,
                              onWheel: (e) => e.target.blur(),
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" || // prevent negative
                                e.key === "e" || // prevent scientific notation
                                e.key === "." || // prevent decimals
                                (e.key === "0" && e.target.value.length === 0) // prevent starting with 0
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                          <Select
                            sx={{
                              width: "34%",
                            }}
                            size="small"
                            value={
                              transferData[st.stage_id]?.followUpUnit || ""
                            }
                            displayEmpty
                            onChange={(e) =>
                              handleInputChange(
                                st.stage_id,
                                "followUpUnit",
                                e.target.value
                              )
                            }
                            error={
                              Boolean(
                                transferData[st.stage_id]?.followUpTime
                              ) && !transferData[st.stage_id]?.followUpUnit
                            }
                          >
                            <MenuItem value="" disabled>
                              Select Unit
                            </MenuItem>
                            <MenuItem value="days">Day</MenuItem>
                            <MenuItem value="hours">Hour</MenuItem>
                            <MenuItem value="minute">Minute</MenuItem>
                          </Select>
                        </Box>
                      </div>
                    </div>

                    <Button
                      sx={{ marginTop: 2 }}
                      color="primary"
                      variant="contained"
                      onClick={() =>
                        isUpdate[s]
                          ? onUpdateTransferTime(st.stage_id)
                          : onSaveTransfertime(st.stage_id)
                      }
                      disabled={!isTransferDataValid(transferData[st.stage_id])}
                    >
                      {isUpdate[s] ? "Update" : "Save"}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleCancel(st.stage_id)}
                      sx={{
                        color: "#666",
                        borderColor: "#ddd",
                        marginTop: 2,
                        marginLeft: 1,
                        "&:hover": {
                          borderColor: "#999",
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <DealEmailTemplate
                      open={openTemplate}
                      close={onEmailtempClose}
                      lead={{}}
                      loadingDU={loading}
                      multiple
                      stage={st}
                    />
                  </Paper>
                )}
              </React.Fragment>
            ))}

            <div>
              <IconButton
                className="stages_add_wrapper"
                onClick={onAddNewStage}
                style={{ padding: 0 }}
                disabled={disableAS}
              >
                <AddCircleOutline />
                <Typography variant="caption" style={{ marginLeft: 5 }}>
                  Add Stage
                </Typography>
              </IconButton>
            </div>
          </Paper>
          {isSaving ? <LinearProgress /> : null}
          <CardActions>
            <Button color="primary" onClick={onFormCancel}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onSaveNewPipeline}
              disabled={disableSave}
            >
              {newPipe ? "  Save Pipeline" : "Update Pipeline"}
            </Button>
          </CardActions>
        </CardContent>
      </Card>
    </>
  );
}

function PipelineComp({ pipeline, dealStages }) {
  const [openPipe, setOpenPipe] = useState(false);
  const [editPipe, setEditPipe] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [count, setCount] = useState(0);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const [transferData, setTransferData] = useState({});
  const bodyTemplates = useSelector((state) => state?.email?.bodyTemplates);

  const onCancelEdit = () => {
    setEditPipe(false);
  };

  const onDeletePipeline = (e) => {
    const deleteBtn = e.currentTarget;

    dispatch(deleteAPipeline(pipeline.pipeline_id)).catch((error) => {
      setCount(error.count);
      setAnchorEl(deleteBtn);
    });
  };

  const onOpnPipelineClick = () => {
    if (!pipeline.pipeline_stages) {
      setLoading(true);
      dispatch(getAPipeline(pipeline.pipeline_id)).then(() => {
        setLoading(false);
      });
    }

    setOpenPipe(!openPipe);
    setEditPipe(false);
  };

  const onPipelineEditClick = () => {
    if (!pipeline.pipeline_stages) {
      setLoading(true);
      dispatch(getAPipeline(pipeline.pipeline_id)).then(() => {
        setLoading(false);
      });
    }

    setEditPipe(!editPipe);
    setOpenPipe(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleTransfer = async (index, stageName) => {
    setExpandedStage((prev) => (prev === index ? null : index));
    dispatch(getStageTransfer(pipeline?.pipeline_id)).then((res) => {
      const { result } = res;
      // Map through the response array and set the transfer data for each relevant stage
      const stageTransferData = result?.reduce((acc, item) => {
        const stageName =
          dealStages.find((stage) => stage.stage_id === item.deal_stage_to)
            ?.stage_name || "Unknown Stage";
        acc[item?.deal_stage_from] = {
          stageTransitionTime: formatTime(item.stage_transition_time),
          followUpTime: formatTime(item.follow_up_time),
          stageName: stageName,
          activeTemplate: item?.name,
          senderName: item?.sender_name,
        };
        return acc;
      }, {});
      // Store the transfer data for the specific stages in state
      setTransferData((prevData) => ({
        ...prevData,
        ...stageTransferData,
      }));

      // Expand the clicked stage
      setExpandedStage(index);
    });
  };

  return (
    <Card className="pipeline_card">
      <div className="pipeline_contents">
        <CardContent>
          <Typography gutterBottom variant="h6" component="h2">
            {pipeline.pipeline_name}
          </Typography>
          <Typography variant="body2" color="textSecondary" component="p">
            Deal rots after {pipeline.deal_expiry} days
          </Typography>
        </CardContent>
        <CardActions>
          <Button size="small" color="primary" onClick={onOpnPipelineClick}>
            {openPipe ? "Close" : "View"}
          </Button>
          <Button size="small" color="primary" onClick={onPipelineEditClick}>
            Edit
          </Button>
          <Button size="small" color="primary" onClick={onDeletePipeline}>
            Delete
          </Button>
          <Popover
            id={pipeline.pipeline_id}
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <Typography style={{ padding: 15, maxWidth: 200 }}>
              Since {count} {count === "1" ? "deal is" : "deals are"} using this
              Pipeline, you cannot delete this pipeline right now.
            </Typography>
          </Popover>
        </CardActions>
      </div>

      <CardContent style={{ padding: "0 16px" }}>
        <Collapse in={openPipe}>
          <Paper className="pipeline_list_wrapper">
            <List className="settings_list">
              {pipeline.pipeline_stages ? (
                pipeline.pipeline_stages.map((stage, s) => (
                  <>
                    <ListItem key={s} className="list_item">
                      <ListItemText
                        primary={<Typography>{stage.stage_name}</Typography>}
                        secondary={
                          <Typography variant="caption">
                            Probability {stage.probability}%
                          </Typography>
                        }
                      />
                      <Button
                        onClick={() =>
                          expandedStage === s
                            ? setExpandedStage(null)
                            : handleToggleTransfer(s, stage)
                        }
                        color="primary"
                        disabled={
                          s === pipeline.pipeline_stages.length - 1 // Disable toggle for the last stage (no "to stage")
                        }
                      >
                        {expandedStage === s
                          ? "Close"
                          : "View Stage email schedule"}
                      </Button>
                    </ListItem>
                    {/* Collapsible section for additional data (visible when expanded) */}
                    <Collapse
                      in={expandedStage === s}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          p: 2,
                          mt: 2,
                          mb: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        {/* Auto-Move Settings */}
                        <Typography gutterBottom>
                          <MoveUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                          Auto-Move Settings
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={1}
                        >
                          <AccessTimeIcon fontSize="small" />
                          <Typography>
                            Move To : &nbsp;
                            {transferData[stage.stage_id]?.stageName ? (
                              transferData[stage.stage_id].stageName
                            ) : (
                              <Tooltip title="No stage has been set for auto-move">
                                <Box component="span" color="gray">
                                  No stage set
                                  {/* <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{ ml: 0.5 }}
                                  /> */}
                                </Box>
                              </Tooltip>
                            )}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={2}
                        >
                          <AccessTimeIcon fontSize="small" />
                          <Typography>
                            After :{" "}
                            {transferData[stage.stage_id]
                              ?.stageTransitionTime ? (
                              transferData[stage.stage_id].stageTransitionTime
                            ) : (
                              <Tooltip title="No transition time configured">
                                <Box component="span" color="gray">
                                  No time set
                                  {/* <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{ ml: 0.5 }}
                                  /> */}
                                </Box>
                              </Tooltip>
                            )}
                          </Typography>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Follow-Up Email Settings */}
                        <Typography gutterBottom>
                          <EmailIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                          Follow-Up Email Settings
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={1}
                        >
                          <EmailIcon fontSize="small" />
                          <Typography>
                            Template :{" "}
                            {transferData[stage?.stage_id]?.activeTemplate ? (
                              transferData[stage?.stage_id]?.activeTemplate
                            ) : (
                              <Tooltip title="No email template selected">
                                <Box component="span" color="gray">
                                  No template set
                                  {/* <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{ ml: 0.5 }}
                                  /> */}
                                </Box>
                              </Tooltip>
                            )}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <ScheduleSendIcon fontSize="small" />
                          <Typography>
                            Send After :{" "}
                            {transferData[stage.stage_id]?.followUpTime ? (
                              transferData[stage.stage_id].followUpTime
                            ) : (
                              <Tooltip title="No follow-up timing configured">
                                <Box component="span" color="gray">
                                  No time set
                                  {/* <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{ ml: 0.5 }}
                                  /> */}
                                </Box>
                              </Tooltip>
                            )}
                          </Typography>
                        </Stack>
                        {transferData[stage.stage_id]?.senderName && (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mt={1}
                          >
                            <BadgeIcon fontSize="small" />
                            <Typography>
                              Email Sender Name :{" "}
                              {transferData[stage.stage_id]?.senderName ? (
                                `${transferData[stage.stage_id].senderName}`
                              ) : (
                                <Tooltip title="No sender name configured">
                                  <Box component="span" color="gray">
                                    No Sender name set
                                    <InfoOutlinedIcon
                                      fontSize="small"
                                      sx={{ ml: 0.5 }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </>
                ))
              ) : (
                <LinearProgress style={{ margin: 3 }} />
              )}
            </List>
          </Paper>
        </Collapse>
        <Collapse in={editPipe}>
          {pipeline.pipeline_stages ? (
            <PipelineForm
              dealStages={dealStages}
              {...pipeline}
              cancel={onCancelEdit}
            />
          ) : null}
          {loading ? <LinearProgress style={{ margin: 3 }} /> : null}
        </Collapse>
      </CardContent>
    </Card>
  );
}

function DealsPipeline() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [openStages, setOpenStages] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const pipelines = useSelector((state) => state.deals.pipelines);
  const dealStages = useSelector((state) => state.deals.dealStages);
  const [dStages, setDStages] = useState(dealStages);
  const loading = useSelector((state) => state.deals.loading);

  useEffect(() => {
    if (openStages) {
      dispatch(getAllDealStages()).then((dealStagesFromStore) => {
        setDStages(dealStagesFromStore);
      });
    }

    if (open) {
      let stagePromise = Promise.resolve();

      if (!dealStages.length) {
        stagePromise = dispatch(getAllDealStages()).then(
          (dealStagesFromStore) => {
            setDStages(dealStagesFromStore);

            return Promise.resolve();
          }
        );
      }

      stagePromise.then(() => {
        dispatch(getDealPipelines());
      });
    }
  }, [open, openStages]);

  const onChangePipelineClick = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const onAddNewClick = () => {
    setAddNew(true);
  };

  const cancelAddNew = () => {
    setAddNew(false);
  };

  const onOpenStagesClick = () => {
    setOpenStages(true);
  };

  const onStagesClose = () => {
    setOpenStages(false);
  };

  const updateDealStagesState = (dss) => {
    setDStages(dss);
  };

  const onCancelNew = (i) => {
    const updatedStages = [...dStages];
    updatedStages.splice(i, 1);

    setDStages(updatedStages);
  };

  const onAddNewStage = () => {
    const updatedStages = [...dStages];
    updatedStages.push({
      stage_name: "",
    });

    setDStages(updatedStages);
  };

  return (
    <div>
      <Card className="list_paper">
        <List className="settings_list">
          <AccessControl name="add_edit_deal_stages">
            <ListItem className="list_item">
              <ListItemText
                primary={<Typography>Deal Stages</Typography>}
                secondary={
                  <Typography variant="caption">
                    You can add or edit Deal stages here.
                  </Typography>
                }
              />
              <Button
                autoFocus
                onClick={onOpenStagesClick}
                color="primary"
                className="sec-button"
              >
                Change
              </Button>
            </ListItem>
          </AccessControl>
          <AccessControl name="add_edit_deal_pipelines">
            <ListItem className="list_item">
              <ListItemText
                primary={<Typography>Pipeline</Typography>}
                secondary={
                  <Typography variant="caption">
                    You can add or edit Pipelines here.
                  </Typography>
                }
              />
              <Button
                autoFocus
                onClick={onChangePipelineClick}
                color="primary"
                className="sec-button"
              >
                Change
              </Button>
            </ListItem>
          </AccessControl>
        </List>
      </Card>
      <Dialog open={openStages} onClose={onStagesClose}>
        <DialogTitle onClose={onStagesClose}>Deal Stages</DialogTitle>
        <Divider />
        <DialogContent>
          {dStages?.length ? (
            dStages.map((ds, l) => (
              <StageItem
                key={l}
                index={l}
                stage={ds}
                isNew={!ds.stage_name}
                cancelNew={onCancelNew}
                addNew={(name) => dispatch(addDealStage(name))}
                update={(stageDetails) =>
                  dispatch(updateDealStage(stageDetails))
                }
                remove={(id) => dispatch(deleteDealStage(id))}
                updateDealStagesState={updateDealStagesState}
                total={dealStages?.length}
              />
            ))
          ) : (
            <Typography
              variant="caption"
              component="div"
              className="empty-message"
            >
              No deal stages available to show...Add one now.
            </Typography>
          )}
          {loading ? <LinearProgress /> : null}
          <div className="role_add_btn_wrapper">
            <Button
              className="role_add_button"
              onClick={onAddNewStage}
              disabled={dealStages?.length !== dStages?.length}
            >
              <AddCircleOutline />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onClose={onClose} scroll="body">
        <DialogTitle onClose={onClose}>Pipelines</DialogTitle>
        <Divider />
        <DialogContent>
          {pipelines.length ? (
            pipelines.map((pl, i) => (
              <PipelineComp key={i} pipeline={pl} dealStages={dealStages} />
            ))
          ) : loading || addNew ? null : (
            <Typography
              variant="caption"
              component="div"
              className="empty-message"
            >
              No pipelines to show...Add one now.
            </Typography>
          )}

          <Collapse in={addNew}>
            <PipelineForm
              dealStages={dealStages}
              cancel={cancelAddNew}
              newPipe
            />
          </Collapse>
        </DialogContent>
        {loading ? <LinearProgress /> : null}
        <DialogActions className="pipeline_add_button_wrapper">
          <IconButton onClick={onAddNewClick} disabled={addNew}>
            <AddCircleOutline fontSize="large" />
          </IconButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DealsPipeline;
